import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/shadcn/accordion";

const Heading = ({ children }: { children: string }) => (
  <p className="mt-4 mb-1 text-sm font-semibold first:mt-0">{children}</p>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <p className="my-1 text-sm text-muted-foreground">{children}</p>
);

const List = ({ children }: { children: React.ReactNode }) => (
  <ul className="my-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">{children}</ul>
);

/** Long-form help, collapsed by default so it doesn't dominate the page. */
export const CalendarSyncHelp = () => (
  <Accordion type="single" collapsible>
    <AccordionItem value="calendar-sync" className="border-none">
      <AccordionTrigger className="py-0 text-sm font-semibold hover:no-underline">
        More detail about calendar syncing
      </AccordionTrigger>
      <AccordionContent className="pt-3 pb-0">
        <Heading>How it works</Heading>
        <Body>
          Once connected, Klipboard CRM and your Microsoft calendar stay in step
          automatically, in both directions. Changes usually appear within a few minutes.
        </Body>

        <Heading>Which appointments are kept in sync?</Heading>
        <Body>Two things to know, and the first covers most situations:</Body>
        <List>
          <li>
            <strong>Anything already syncing stays syncing</strong>, in both directions,
            whatever date it's on or moves to. Appointments never drop out over time.
          </li>
          <li>
            <strong>New appointments created in your calendar</strong> are picked up
            automatically when they fall within about the next year.
          </li>
        </List>
        <Body>
          Appointments you create in Klipboard CRM with calendar sync enabled are sent to
          your calendar at the next sync, whatever date they're for - there's no limit
          on that direction.
        </Body>

        <Heading>Why isn't my existing calendar in Klipboard CRM?</Heading>
        <Body>
          Connecting your calendar does not import existing appointments. To record an older
          appointment in Klipboard CRM, create a task and leave
          <strong> "Appointment (Sync with External Calendar)" unticked</strong>. This records
          it in CRM without creating another calendar entry. It will not be linked to the
          existing Outlook appointment or kept in sync with it.
        </Body>
        <Body>
          Enabling calendar sync on the new task creates a separate appointment, not a link
          to the existing one. This can create a duplicate in Outlook and send new
          invitations to any recipients you add.
        </Body>

        <Heading>I created an appointment a long way ahead and it hasn't appeared</Heading>
        <Body>
          If you created it in your calendar rather than in Klipboard CRM, it may be further
          ahead than we currently watch. It isn't lost - it'll start syncing nearer the time,
          automatically.
        </Body>
        <Body>
          If you need to record it in Klipboard CRM now, create a task and leave
          <strong> "Appointment (Sync with External Calendar)" unticked</strong>. This creates
          a separate CRM-only record; it does not link to the existing Outlook appointment.
          Recreating it with sync enabled can create a duplicate and send new invitations.
        </Body>

        <Heading>Do older appointments stop syncing over time?</Heading>
        <Body>
          No. Once an appointment is syncing it keeps syncing, however old it gets. Nothing is
          removed from Klipboard CRM because of its age.
        </Body>

        <Heading>Do I need to reconnect or renew anything?</Heading>
        <Body>
          No. The range we watch moves forward automatically so it always covers at least the
          year ahead. There's nothing to reconnect, renew or approve.
        </Body>

        <Heading>A change I made hasn't come across</Heading>
        <Body>Give it a few minutes first. If it still hasn't appeared:</Body>
        <List>
          <li>
            If the appointment has <strong>never</strong> synced before, check it isn't dated
            well in the future - those are picked up nearer the time - or before you connected.
          </li>
          <li>
            If it <strong>has</strong> synced before, it should still be syncing. Contact support.
          </li>
        </List>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);
